import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { baseUrlApi } from '../../utils/url';
import { RootState } from './store';

export interface LocalAccessory {
  id: number;
  name: string;
  quantity: number;
  state: string;       // '1'=BE '2'=EM '3'=ME ''=non renseigné
  observations: string;
  LocalId: number;
}

interface LocalAccessoryState {
  accessories: LocalAccessory[];
  loading: boolean;
  error: string | null;
}

const initialState: LocalAccessoryState = {
  accessories: [],
  loading: false,
  error: null,
};

const getToken = (state: RootState) => (state as any).authentification?.user?.token;

export const fetchAccessoriesByLocal = createAsyncThunk<LocalAccessory[], number, { state: RootState }>(
  'localAccessory/fetchByLocal',
  async (localId, { getState, rejectWithValue }) => {
    try {
      const res = await axios.get(`${baseUrlApi}/localaccessories/local/${localId}`, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch accessories');
    }
  }
);

export const initAccessoryDefaults = createAsyncThunk<LocalAccessory[], number, { state: RootState }>(
  'localAccessory/initDefaults',
  async (localId, { getState, rejectWithValue }) => {
    try {
      const res = await axios.post(`${baseUrlApi}/localaccessories/local/${localId}/init`, {}, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to init defaults');
    }
  }
);

export const createAccessory = createAsyncThunk<LocalAccessory, Partial<LocalAccessory>, { state: RootState }>(
  'localAccessory/create',
  async (data, { getState, dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(`${baseUrlApi}/localaccessories`, data, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      if (data.LocalId) dispatch(fetchAccessoriesByLocal(data.LocalId));
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create accessory');
    }
  }
);

export const updateAccessory = createAsyncThunk<
  LocalAccessory,
  { id: number; data: Partial<LocalAccessory>; localId: number },
  { state: RootState }
>(
  'localAccessory/update',
  async ({ id, data, localId }, { getState, dispatch, rejectWithValue }) => {
    try {
      const res = await axios.put(`${baseUrlApi}/localaccessories/${id}`, data, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      dispatch(fetchAccessoriesByLocal(localId));
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update accessory');
    }
  }
);

export const deleteAccessory = createAsyncThunk<
  void,
  { id: number; localId: number },
  { state: RootState }
>(
  'localAccessory/delete',
  async ({ id, localId }, { getState, dispatch, rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrlApi}/localaccessories/${id}`, {
        headers: { Authorization: `bearer ${getToken(getState())}` },
      });
      dispatch(fetchAccessoriesByLocal(localId));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete accessory');
    }
  }
);

const localAccessorySlice = createSlice({
  name: 'localAccessory',
  initialState,
  reducers: {
    clearAccessories: (state) => { state.accessories = []; },
  },
  extraReducers: (builder) => {
    const pending  = (state: LocalAccessoryState) => { state.loading = true; state.error = null; };
    const rejected = (state: LocalAccessoryState, action: any) => { state.loading = false; state.error = action.payload; };

    builder
      .addCase(fetchAccessoriesByLocal.pending,  pending)
      .addCase(fetchAccessoriesByLocal.rejected, rejected)
      .addCase(fetchAccessoriesByLocal.fulfilled, (state, action) => {
        state.loading      = false;
        state.accessories  = action.payload;
      })
      .addCase(initAccessoryDefaults.pending,  pending)
      .addCase(initAccessoryDefaults.rejected, rejected)
      .addCase(initAccessoryDefaults.fulfilled, (state, action) => {
        state.loading      = false;
        state.accessories  = action.payload;
      });
  },
});

export const { clearAccessories } = localAccessorySlice.actions;
export default localAccessorySlice.reducer;
